import type { RouteMeta, RouteRecordRaw } from 'vue-router'

import { keys } from '@/app/i18n'
import type { BreadcrumbItem } from '@/app/router/meta'

/**
 * The finances module's routes (WP-10, wp/finances §3): the seven manifest rows, paths and names from it (D-15); the
 * legacy paths (`/ac/finances/create/income/`, `/ac/finances/edit/expense/:id`, …) reach them through the generated
 * legacy redirects. The previews (`finance-preview`, `expense-preview`) are WP-08's page; `/ac/payments` redirects to
 * `/ac/invoices` (D-39).
 *
 * Guards (D-21, OPEN-12 (a)): the overview takes Finances read (legacy nav); the editors Invoicing write and Purchase
 * invoices Invoicing read — the permissions the API asks (legacy `meta.module` / `mode` were never evaluated). Pages
 * that mount WP-08's payment drawers load the invoices catalogue too (`catalogues`).
 * `/ac/dashboard` (2026-10-26, owner's request) is the Accounting dashboard, the widget board of the design canvas
 * "Skileo Accounting Board", and the Accounting home (`getHomeRoute` builds `/<software>/dashboard`); legacy only
 * redirected it to `/ac/finances`. No `resource`, like the other softwares' dashboards: it is the home of every
 * Accounting user, and the board hides each card its viewer may not read.
 * NEW-27: the overview's crumb is the current page (legacy linked `/ac/accounting`, a 404); Purchase invoices is titled
 * and crumbed "Purchase invoices" (legacy "Purchase invoices invoices" / "Sales invoices").
 */
const crumb = (text: string, to?: string): BreadcrumbItem => (to ? { text, to } : { text, active: true })
const finances = crumb(keys.t('common.nav.finances'), '/ac/finances')

const FinancesBoardView = () => import('./views/FinancesBoardView.vue')
const FinancesOverviewView = () => import('./views/FinancesOverviewView.vue')
const ExpenseListView = () => import('./views/ExpenseListView.vue')
const FinanceRecordEditView = () => import('./views/FinanceRecordEditView.vue')

const overview: RouteMeta = {
  software: 'ac',
  resource: 'finances',
  pageTitle: keys.t('finances.overview.title.page'),
  breadcrumb: [crumb(keys.t('finances.overview.title.overview'))],
  catalogues: ['invoices'],
}

const editor = (pageTitle: string, current: string): RouteMeta => ({
  software: 'ac',
  resource: 'invoices',
  action: 'write',
  navActiveLink: 'finances',
  pageTitle,
  breadcrumb: [finances, crumb(current)],
})

const routes: RouteRecordRaw[] = [
  // The Accounting dashboard: no breadcrumb, the board greets instead.
  { path: '/ac/dashboard', name: 'ac_dashboard', component: FinancesBoardView, meta: { software: 'ac' } },
  { path: '/ac/finances', name: 'finances', component: FinancesOverviewView, meta: { ...overview } },
  // Only the two lists: another word is the 404 page, not an empty overview.
  {
    path: '/ac/finances/:listMode(documents|payments)',
    name: 'finances-list',
    // the router ignores case: `/ac/finances/Payments` would match and draw no list (the view takes lower case only)
    sensitive: true,
    component: FinancesOverviewView,
    meta: { ...overview },
  },
  {
    path: '/ac/finances/incomes/new',
    name: 'create-income',
    component: FinanceRecordEditView,
    meta: editor(keys.t('finances.edit.title.addIncome'), keys.t('finances.edit.title.addIncome')),
  },
  {
    path: '/ac/finances/incomes/:id(\\d+)/edit',
    name: 'edit-income',
    component: FinanceRecordEditView,
    meta: editor(keys.t('finances.route.title.editIncome'), keys.t('finances.route.title.editIncome')),
  },
  {
    path: '/ac/finances/expenses/new',
    name: 'create-expense',
    component: FinanceRecordEditView,
    meta: editor(keys.t('finances.edit.title.addExpense'), keys.t('finances.edit.title.addExpense')),
  },
  {
    path: '/ac/finances/expenses/:id(\\d+)/edit',
    name: 'edit-expense',
    component: FinanceRecordEditView,
    meta: editor(keys.t('finances.edit.title.editExpense'), keys.t('finances.edit.title.editExpense')),
  },
  {
    path: '/ac/expenses',
    name: 'expenses',
    component: ExpenseListView,
    meta: {
      software: 'ac',
      resource: 'invoices',
      pageTitle: keys.t('common.nav.purchaseInvoices'),
      breadcrumb: [crumb(keys.t('common.nav.purchaseInvoices'))],
      catalogues: ['invoices'],
    },
  },
]

export default routes
